import React, { useState } from 'react';
import { Typography, Button, Box, TableBody, Table } from '@material-ui/core';
import { useLocation, useParams } from 'react-router-dom';
import { useStoreState } from 'easy-peasy';

import { useTranslations } from 'utils/useTranslations';

import { AssignCatalogItemModal } from 'components/AssignCatalogItem';

import usePermissions from 'utils/usePermissions';
import { roundTo2 } from 'utils/roundTo2';
import { ChangePriceModal } from 'components/Modals/ChangePriceModal';
import ResolveRequest from 'components/Modals/ResolveRequest';
import MedicalRecordSKUTableHead from './MedicalRecordSKUTableHead';
import MedicalRecordSKUTableRow from './MedicalRecordSKUTableRow';
import { useStyles } from './styles';

const MedicalRecordSKUTable = ({ catalog }: { catalog: any }) => {
  const intl = useTranslations();
  const styles = useStyles();
  const { patient } = useParams<{ patient: string }>();
  const user = useStoreState((state) => state.user);
  const [modalOpened, setModalOpened] = useState(false);
  const [changePriceModalOpened, setChangePriceModalOpened] = useState(false);
  const [selectedSkuItem, setSelectedSkuItem] = useState<any>({});
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const {
    canAddSkuInBills,
    canAddSkuInMedicalRecord,
    canCheckmarkExamSkuInBills,
    canCheckmarkSkuInBills,
    canCheckoutAddAndViewPaymentsInBills,
  } = usePermissions();
  const location = useLocation();

  const [resolveModalInfo, setResolveModalInfo] = useState({
    open: false,
    catalogItemId: null,
    medicalRecordId: null,
  });

  const canBeResolved = location.pathname.includes('accounts');

  // const pharmacistView = canBeResolved && user.type === UserTypeEnum.pharmacist;

  // const adminsView = [UserTypeEnum.admin, UserTypeEnum.doctor].includes(user.type);

  const toggleModal = () => setModalOpened((old) => !old);
  const handleModalOpen = (subcategory) => () => {
    setSelectedSubcategory(subcategory);
    toggleModal();
  };

  const toggleChangePriceModal = () => setChangePriceModalOpened((old) => !old);
  const handleChangePriceModalOpen = (skuItem) => () => {
    setSelectedSkuItem(skuItem);
    toggleChangePriceModal();
  };

  // const technicianPermissions = getPermissions(user.type, [UserTypeEnum.technician_labs, UserTypeEnum.technician_radiology]);

  const exams = catalog?.filter((cItem: any) => cItem.subcategory === 'exam') || [];
  const supplies = catalog?.filter((cItem: any) => cItem.subcategory === 'supply') || [];
  const services = catalog?.filter((cItem: any) => cItem.subcategory === 'service') || [];
  const medicaments = catalog?.filter((cItem: any) => cItem.subcategory === 'medicine') || [];

  const calculateTotal = (arr: any[] = []) => {
    return roundTo2(arr.reduce((sum, sItem) => sum + sItem.precio * sItem.amount, 0));
  };

  const handleResolve = ({ catalogItemId, medicalRecordId }: { catalogItemId: string; medicalRecordId: string }) => {
    setResolveModalInfo({ open: true, catalogItemId, medicalRecordId });
  };
  const handleResolveClose = () => {
    setResolveModalInfo({ open: false, catalogItemId: null, medicalRecordId: null });
  };

  const canAddSku = canBeResolved ? canAddSkuInBills : canAddSkuInMedicalRecord(patient);

  return (
    <>
      {/* Services */}
      <Box className={styles.sectionHeader}>
        <Typography className={styles.sectionHeaderBold} variant="h5">
          {intl('services')}
        </Typography>
        {canAddSku && (
          <Button onClick={handleModalOpen('service')} variant="contained" color="primary">
            {intl('services.add_new_item')}
          </Button>
        )}
      </Box>
      <Table>
        <MedicalRecordSKUTableHead canBeResolved={canBeResolved && canCheckmarkSkuInBills} />
        <TableBody>
          {services &&
            services.map((serviceItem) => (
              <MedicalRecordSKUTableRow
                onChangePrice={handleChangePriceModalOpen(serviceItem)}
                canBeResolved={canBeResolved && canCheckmarkSkuInBills}
                row={serviceItem}
                key={serviceItem._id}
              />
            ))}
        </TableBody>
      </Table>
      {canBeResolved && services.length > 0 && canCheckoutAddAndViewPaymentsInBills && (
        <Box mt={2}>
          <Typography variant="h6">
            {intl('shared.subtotal')}: {calculateTotal(services)}
          </Typography>
        </Box>
      )}
      {/* Exams */}
      <Box className={styles.sectionHeader}>
        <Typography className={styles.sectionHeaderBold} variant="h5">
          {intl('exams')}
        </Typography>
        {canAddSku && (
          <Button onClick={handleModalOpen('exam')} variant="contained" color="primary">
            {intl('exam.add_new_exam')}
          </Button>
        )}
      </Box>
      <Table>
        <MedicalRecordSKUTableHead canBeResolved={canBeResolved && (canCheckmarkExamSkuInBills || canCheckmarkSkuInBills)} />
        <TableBody>
          {exams &&
            exams.map((examItem) => (
              <MedicalRecordSKUTableRow
                canBeResolved={canBeResolved && (canCheckmarkExamSkuInBills || canCheckmarkSkuInBills)}
                row={examItem}
                key={examItem._id}
                onChangePrice={handleChangePriceModalOpen(examItem)}
                onResolveRequest={handleResolve}
              />
            ))}
        </TableBody>
      </Table>
      {canBeResolved && exams.length > 0 && canCheckoutAddAndViewPaymentsInBills && (
        <Box mt={2}>
          <Typography variant="h6">
            {intl('shared.subtotal')}: {calculateTotal(exams)}
          </Typography>
        </Box>
      )}
      {/* Medicaments */}
      <Box className={styles.sectionHeader}>
        <Typography className={styles.sectionHeaderBold} variant="h5">
          {intl('medicaments')}
        </Typography>
        {canAddSku && (
          <Button onClick={handleModalOpen('medicine')} variant="contained" color="primary">
            {intl('medicament.add_new_item')}
          </Button>
        )}
      </Box>
      <Table>
        <MedicalRecordSKUTableHead canBeResolved={canBeResolved && canCheckmarkSkuInBills} />
        <TableBody>
          {medicaments &&
            medicaments.map((medicamentItem) => (
              <MedicalRecordSKUTableRow
                canBeResolved={canBeResolved && canCheckmarkSkuInBills}
                row={medicamentItem}
                onChangePrice={handleChangePriceModalOpen(medicamentItem)}
                key={medicamentItem._id}
              />
            ))}
        </TableBody>
      </Table>
      {canBeResolved && medicaments.length > 0 && canCheckoutAddAndViewPaymentsInBills && (
        <Box mt={2}>
          <Typography variant="h6">
            {intl('shared.subtotal')}: {calculateTotal(medicaments)}
          </Typography>
        </Box>
      )}
      {/* Supplies */}
      <Box className={styles.sectionHeader}>
        <Typography className={styles.sectionHeaderBold} variant="h5">
          {intl('supplies')}
        </Typography>
        {canAddSku && (
          <Button onClick={handleModalOpen('supply')} variant="contained" color="primary">
            {intl('supply.add_new_item')}
          </Button>
        )}
      </Box>
      <Table>
        <MedicalRecordSKUTableHead canBeResolved={canBeResolved && canCheckmarkSkuInBills} />
        <TableBody>
          {supplies &&
            supplies.map((supplyItem) => (
              <MedicalRecordSKUTableRow
                canBeResolved={canBeResolved && canCheckmarkSkuInBills}
                onChangePrice={handleChangePriceModalOpen(supplyItem)}
                row={supplyItem}
                key={supplyItem._id}
              />
            ))}
        </TableBody>
      </Table>
      {canBeResolved && canCheckoutAddAndViewPaymentsInBills && supplies.length > 0 && (
        <Box mt={2}>
          <Typography variant="h6">
            {intl('shared.subtotal')}: {calculateTotal(supplies)}
          </Typography>
        </Box>
      )}

      <AssignCatalogItemModal toggle={toggleModal} open={modalOpened} selectedSubcategory={selectedSubcategory} />
      <ChangePriceModal onClose={toggleChangePriceModal} open={changePriceModalOpened} skuItem={selectedSkuItem} />
      <ResolveRequest
        onClose={handleResolveClose}
        open={resolveModalInfo.open}
        catalogItemId={resolveModalInfo.catalogItemId}
        medicalRecordId={resolveModalInfo.medicalRecordId}
      />
    </>
  );
};

export default MedicalRecordSKUTable;
